/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  EducationSessionRecordState,
  Challenge,
  CompetenceAnalysisState,
  ParametricResultDescription,
  ChallengeMode,
  FeedbackStatusEnumType,
} from "../../schema";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import isEqual from "lodash/isEqual";
import {useLazyQuery, useQuery} from "@apollo/client";
import Layout, {Header, Main} from "../../components/Layout";
import classes from "./player.module.css";
import Container from "../../ui/adaptivecontainer";
import Results from "../../components/player/Results";
import {PlayerReduxState} from "../../redux/player/types";
import {getAdditionalScenarios, getCurrentScenario, getSessionId} from "../../redux/player/selectors";
import {postMessageOpener} from "../../utils";
import SessionActionsList from "../../components/results/SessionActionsList";
import {
  nextChallengeQuery,
  NextChallengeQueryData,
  NextChallengeQueryVars,
  resultsQuery,
  ResultsQueryData,
  ResultsQueryVars,
} from "./player.graphql";
import {parametricDescriptionsQuery} from "./results.graphql";
import {analyticsSendEvent, toEventData} from "../../libs/analytics";
import {getSharedId} from "../../types";
import cn from "classnames";
import ReportSaveDropdown from "../../components/results/ReportSaveDropdown";
import Button from "../../ui/button";
import {generateEducationSessionResultsReportPDF} from "../../utils/player/results.pdfGenerator";
import {useCurrentUser} from "../../App.context";

export default function PlayerResultsScreen({challenge, headless}: {challenge: Challenge; headless?: boolean}) {
  const {t} = useTranslation();
  const user = useCurrentUser();
  const showDetailedResults = challenge.showDetailedResults;

  const {sessionId, externalUserId, isExternal, actions, additionalScenarios, isInterrupted, isShared} = useSelector(
    PlayerResultsScreen.selector,
    isEqual,
  );

  const {projectId} = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      projectId: params.get("projectId"),
    };
  }, []);

  const handleFinish = useCallback(() => {
    if (isExternal) {
      postMessageOpener({type: "Finish"});
    }
  }, [isExternal]);

  const {data: parametricDescriptionsData} = useQuery<{descriptions: ParametricResultDescription[]}>(
    parametricDescriptionsQuery,
    {
      variables: {
        challengeId: challenge.id,
      },
    },
  );

  const parametricDescriptions = parametricDescriptionsData?.descriptions;

  const {data: nextChallengeData} = useQuery<NextChallengeQueryData, NextChallengeQueryVars>(nextChallengeQuery, {
    variables: {
      challengeId: isShared ? getSharedId(challenge) : challenge.id,
      projectId: projectId ?? undefined,
    },
  });

  const nextChallenge = nextChallengeData?.nextChallenge;

  const [query, {data, stopPolling}] = useLazyQuery<ResultsQueryData, ResultsQueryVars>(resultsQuery, {
    variables: {
      sessionId: sessionId!,
      externalUserId: externalUserId,
      challengeId: challenge.id,
    },
    pollInterval: 5000,
  });

  const results = data?.results;
  const descriptions = data?.resultDescriptions;

  useEffect(() => {
    setTimeout(() => query(), 500);
  }, [query]);

  const {recordState, record, competenceAnalysisState, competenceResults, isUserInputAnalysisReady, resultFeedback} =
    results ?? {};

  const [isResultsSent, setResultsSent] = useState(false);

  useEffect(() => {
    if (!results || isResultsSent) {
      return;
    }
    const {actionResults, ...pickedResult} = results;
    analyticsSendEvent("playerResult", {
      sessionId: sessionId!,
      externalUserId: String(externalUserId),
      ...toEventData(pickedResult),
    });
    setResultsSent(true);
  }, [results, isResultsSent, sessionId, externalUserId]);

  // stop polling to get results
  useEffect(() => {
    let doPollingInCompetenceTest =
      challenge.mode === ChallengeMode.COMPETENCE_TEST &&
      (competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS ||
        (resultFeedback && resultFeedback.feedbackStatus === FeedbackStatusEnumType.IN_PROGRESS));

    let doPollingInRpg =
      challenge.mode === ChallengeMode.RPG_MODE &&
      (competenceAnalysisState === CompetenceAnalysisState.IN_PROGRESS ||
        (resultFeedback && resultFeedback.feedbackStatus === FeedbackStatusEnumType.IN_PROGRESS));

    let doStopPolling =
      recordState !== undefined &&
      [
        EducationSessionRecordState.READY,
        EducationSessionRecordState.FAILED,
        EducationSessionRecordState.SKIPPED,
      ].includes(recordState) &&
      !doPollingInCompetenceTest &&
      !doPollingInRpg &&
      isUserInputAnalysisReady;

    if (doStopPolling) {
      analyticsSendEvent("playerResultRecordStateUpdate", {
        recordState,
        recordURL: String(record),
      });

      analyticsSendEvent("playerResultCompetenceUpdate", {
        competenceAnalysisState,
        competenceResults: competenceResults?.join(", "),
      });

      stopPolling && stopPolling();
    }
  }, [
    recordState,
    record,
    competenceAnalysisState,
    competenceResults,
    isUserInputAnalysisReady,
    stopPolling,
    challenge.mode,
    resultFeedback,
    resultFeedback?.feedbackStatus,
  ]);

  if (!results) {
    return null;
  }

  const resultsClassName = showDetailedResults
    ? classes.resultsContainer
    : cn(classes.resultsContainerCenter, classes.resultsContainer);

  const summrayClassName = showDetailedResults
    ? classes.summaryContainer
    : cn(classes.summaryContainerCenter, classes.summaryContainer);

  return (
    <Layout player className={classes.resultsLayout}>
      {!headless && <Header type={"filled"} hideLinks adaptive />}
      <Main>
        <Container className={resultsClassName}>
          <div className={summrayClassName}>
            <Results
              challenge={challenge}
              projectId={projectId ?? undefined}
              results={results}
              onFinish={handleFinish}
              nextChallenge={nextChallenge}
              descriptions={descriptions}
              parametricDescriptions={parametricDescriptions}
              sessionId={sessionId!}
            />
          </div>
          {!isInterrupted && showDetailedResults ? (
            <div className={classes.detailedContainer}>
              <div className={classes.titleWithButton}>
                <h2 className={classes.title}>{t("player.results.detailed")}</h2>
                <ReportSaveDropdown
                  user={user}
                  challenge={challenge}
                  actions={actions}
                  additionalScenarios={additionalScenarios}
                  results={{isContentMatches: true, ...results}}
                  resultDescriptions={descriptions}
                  parametricResultDescriptions={parametricDescriptions}
                />
              </div>
              <div className={classes.actionsListContainer}>
                <SessionActionsList
                  actionsResults={results.actionResults}
                  initAvatarAction={results.initAvatarAction}
                  challenge={challenge}
                  actions={actions}
                  additionalScenarios={additionalScenarios}
                  finishedScenarios={results.finishedScenariosIds}
                />
              </div>
            </div>
          ) : (
            <div className={classes.downloadLink}>
              <Button
                color="danger"
                size="m"
                onClick={() =>
                  generateEducationSessionResultsReportPDF(
                    {
                      user,
                      challenge,
                      actions,
                      additionalScenarios,
                      results: {isContentMatches: true, ...results},
                      resultDescriptions: descriptions,
                      parametricResultDescriptions: parametricDescriptions,
                    },
                    t,
                  )
                }
              >
                {t("player.results.download")} {"PDF"}
              </Button>
            </div>
          )}
        </Container>
      </Main>
    </Layout>
  );
}

PlayerResultsScreen.selector = (state: PlayerReduxState) => ({
  sessionId: getSessionId(state),
  isExternal: state.isExternal,
  externalUserId: state.externalUserId,
  actions: getCurrentScenario(state).actions,
  additionalScenarios: getAdditionalScenarios(state),
  isInterrupted: state.interrupted,
  isShared: getSharedId(state.challenge),
});
